import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {ClientConfigurationScheme, ComponentConfiguration, ComponentInit, InitialConfig} from '@twpub/core/utils';
import {FieldProps, Language, LanguageSelector} from '@twpub/core/models';
import {ComponentCategory, ControlType} from '@twpub/core/enums';
import {BaseConfigurableComponent} from '@twpub/shared/components/base';
import {MatButtonToggle, MatButtonToggleGroup} from '@angular/material/button-toggle';

import {MatButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatIcon} from '@angular/material/icon';

@Component({
  selector: 'pub-language-radio',
  templateUrl: './language-radio.component.html',
  styleUrls: ['./language-radio.component.scss'],
  standalone: true,
  imports: [MatButtonToggleGroup, MatButtonToggle, MatButton, MatTooltip, MatMenuTrigger, MatIcon, MatMenu, MatMenuItem]
})
export class LanguageRadioComponent extends BaseConfigurableComponent implements LanguageSelector {
  static readonly compName = 'LanguageRadioComponent';
  static readonly DEFAULT_VALUE = 'defaultValue';

  @Input() selectedLangCodes?: string[];
  @Input() langs: Language[] = [];

  @Output() selectLanguages: EventEmitter<string[]> = new EventEmitter<string[]>()

  @ViewChild(MatButtonToggleGroup) toggleGroup!: MatButtonToggleGroup;

  readonly MAX_BUTTONS = 5;

  static Init = class extends ComponentInit {
    readonly scheme = ClientConfigurationScheme.DEFAULT;
    override props: FieldProps[] = [this.LABEL_FIELD, this.VISIBLE_FIELD,
      {
        key: LanguageRadioComponent.DEFAULT_VALUE,
        label: 'Default language code',
        controlType: ControlType.Textbox,
        description: 'Separate multiple codes with semicolon'
      }
    ];

    constructor() {
      super(LanguageRadioComponent, LanguageRadioComponent.compName, 'Radio Buttons', [ComponentCategory.SourceLanguageSelector, ComponentCategory.TargetLanguageSelector]);
    }

    createConfig(initialConfig: InitialConfig = undefined) {
      return new ComponentConfiguration<LanguageSelector>(LanguageRadioComponent, this.name, initialConfig);
    }
  }

  private filterExisting = (langs: string[] | undefined) => {
    if (!langs?.length) return undefined;
    const validLanguages = langs.filter(code =>
      this.langs?.some(l => l.code === code)
    );
    return validLanguages.length < langs.length ? validLanguages : langs;
  };

  override doUpdate() {
    super.doUpdate();
    let newSelectedLangs = this.filterExisting(this.selectedLangCodes);
    if (!newSelectedLangs?.length) {
      const defaultLangs = this.config?.getStringArray(LanguageRadioComponent.DEFAULT_VALUE, undefined);
      newSelectedLangs = this.filterExisting(defaultLangs?.slice(0, 1) || []);
    }
    if (!newSelectedLangs?.length) {
      newSelectedLangs = this.langs?.[0] ? [this.langs[0].code] : [];
    }
    if (newSelectedLangs !== this.selectedLangCodes) {
      this.selectedLangCodes = newSelectedLangs;
      this.selectLanguages.emit(newSelectedLangs);
    }
  }

  isLanguageSelected(langCode: string) {
    return this.selectedLangCodes?.includes(langCode);
  }

  onSelect(langCode: string) {
    this.selectedLangCodes = [langCode];
    if (this.toggleGroup) {
      this.toggleGroup.value = langCode;
    }
    this.selectLanguages.emit([langCode]);
  }
}
